import { forwardRef } from 'react';
import { useSkin } from '@/store/SkinContext';
import { SKIN_ICON } from '@/components/AppShell';
import { categoriaDeAtleta, formatMarca, labelPrueba, type AtletaPbSb } from '@/lib/atletas';

export type TipoMarca = 'pb' | 'sb';
export type PosV = 'arriba' | 'abajo';
export type PosH = 'izquierda' | 'derecha';

/** Formatos de salida: 16:9 (como la imagen del podio) y su inverso 9:16. */
export const CARD_DIMS = {
  '16:9': { w: 1200, h: 675 },
  '9:16': { w: 1080, h: 1920 },
} as const;
export type Formato = keyof typeof CARD_DIMS;

interface Props {
  atleta: AtletaPbSb;
  prueba: string;
  tipo: TipoMarca;
  formato: Formato;
  posV: PosV;
  posH: PosH;
  /** Marca + icono mundoatletismo por encima (arriba) o por debajo (abajo)
   *  del bloque de datos. */
  branding: PosV;
  /** Multiplicador del tamaño de texto (1 = base). */
  escala: number;
  /** Contraste de los textos secundarios (0 = gris actual, 1 = casi negro). */
  contraste: number;
  /** Foto YA recortada al recuadro (cover) por el llamante. Se rellena tal
   *  cual — html2canvas no respeta object-fit, por eso se recorta antes. */
  fotoSrc?: string;
  /** Logo de cliente opcional + su esquina (distinta a la de los datos). */
  logoDataUrl?: string;
  logoV: PosV;
  logoH: PosH;
  /** Multiplicador del tamaño del logo de cliente (1 = base). */
  logoEscala: number;
  /** Modo overlay: fondo transparente y sin foto, para rasterizar las capas
   *  del vídeo. overlayPart elige qué se incluye: 'datos' (scrim + datos) o
   *  'logo' (solo el logo). Sin overlay se pinta todo (card completo). */
  overlay?: boolean;
  overlayPart?: 'datos' | 'logo';
}

/** Card de ficha de atleta (DOM, se rasteriza con html2canvas). Usa los
 *  tokens --ld-* de la skin activa. Solo muestra PB o SB de una prueba. */
export const AtletaCard = forwardRef<HTMLDivElement, Props>(function AtletaCard(
  { atleta, prueba, tipo, formato, posV, posH, branding, escala, contraste, fotoSrc, logoDataUrl, logoV, logoH, logoEscala, overlay = false, overlayPart },
  ref,
) {
  const { skin } = useSkin();
  const { w, h } = CARD_DIMS[formato];
  const valor = (tipo === 'pb' ? atleta.pb : atleta.sb)?.[prueba];
  const marca = formatMarca(prueba, valor);
  const categoria = categoriaDeAtleta(atleta);
  const partes = atleta.atleta.trim().split(/\s+/);
  const nombre = partes[0] ?? '';
  const apellidos = partes.slice(1).join(' ');
  const etiqueta = tipo === 'pb' ? 'MEJOR MARCA' : 'MEJOR DE TEMPORADA';

  const arriba = posV === 'arriba';
  const izquierda = posH === 'izquierda';
  const fs = (px: number) => Math.round(px * escala);
  // Textos secundarios: tinta a opacidad variable (gris→negro) en vez del
  // color muted fijo, para poder subir el contraste sobre fotos claras.
  const mutedOp = 0.6 + 0.4 * contraste;

  const brandRow = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexDirection: izquierda ? 'row' : 'row-reverse',
        fontFamily: 'var(--ld-font-mono)',
        fontSize: fs(22),
        letterSpacing: '0.18em',
        color: 'var(--ld-text)',
      }}
    >
      <span style={{ opacity: mutedOp }}>mundoatletismo.es</span>
      <span style={{ flex: 1, height: 2, background: 'var(--ld-line-strong, var(--ld-line))' }} />
      <img src={SKIN_ICON[skin]} alt="" width={fs(40)} height={fs(40)} style={{ display: 'block' }} />
    </div>
  );

  const cuerpo = (
    <>
      <div
        style={{
          fontFamily: 'var(--ld-font-display)',
          fontWeight: 800,
          fontSize: fs(72),
          lineHeight: 1.02,
          letterSpacing: '0.01em',
          textTransform: 'uppercase',
        }}
      >
        <span style={{ color: 'var(--ld-text)' }}>{nombre}</span>
        {apellidos ? <span style={{ color: 'var(--ld-accent)' }}> {apellidos}</span> : null}
      </div>
      {categoria ? (
        <div style={{ fontFamily: 'var(--ld-font-sans)', fontWeight: 600, fontSize: fs(28), color: 'var(--ld-text)' }}>
          {categoria}
        </div>
      ) : null}
      <div
        style={{
          fontFamily: 'var(--ld-font-mono)',
          fontSize: fs(22),
          letterSpacing: '0.14em',
          color: 'var(--ld-text)',
          opacity: mutedOp,
          textTransform: 'uppercase',
          marginTop: fs(6),
        }}
      >
        {etiqueta} · {labelPrueba(prueba)}
      </div>
      <div
        style={{
          fontFamily: 'var(--ld-font-display)',
          fontWeight: 800,
          fontSize: fs(120),
          lineHeight: 0.95,
          color: 'var(--ld-accent)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {marca || '—'}
      </div>
    </>
  );

  const datos = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: fs(10) }}>
      {branding === 'arriba' ? (
        <>
          {brandRow}
          {cuerpo}
        </>
      ) : (
        <>
          {cuerpo}
          {brandRow}
        </>
      )}
    </div>
  );

  // Esquina del logo (debe ser distinta a la de los datos; lo garantiza la vista).
  const logoMax = Math.round(Math.min(w, h) * 0.18 * logoEscala);
  // Degradado de la foto hacia el color del tema, en el lado de los datos:
  // ayuda a la legibilidad sin tapar la foto (más corto en 9:16, más alto en 16:9).
  const gradStop = formato === '9:16' ? 40 : 62;
  const showData = !overlay || overlayPart !== 'logo';
  const showLogo = (!overlay || overlayPart !== 'datos') && !!logoDataUrl;
  const showScrim = overlay ? showData : !!fotoSrc;
  const scrim = showScrim
    ? `linear-gradient(${arriba ? 'to bottom' : 'to top'}, var(--ld-bg) 0%, transparent ${gradStop}%)`
    : 'none';

  return (
    <div
      ref={ref}
      style={{
        width: w,
        height: h,
        position: 'relative',
        overflow: 'hidden',
        background: overlay
          ? 'transparent'
          : fotoSrc
            ? 'var(--ld-bg)'
            : 'linear-gradient(180deg, var(--ld-bg-grad-top) 0%, var(--ld-bg-grad-bot) 100%)',
        color: 'var(--ld-text)',
        fontFamily: 'var(--ld-font-sans)',
      }}
    >
      {fotoSrc && !overlay ? (
        <img
          src={fotoSrc}
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : null}

      {/* Degradado de legibilidad hacia el color del tema. */}
      {showScrim ? <div style={{ position: 'absolute', inset: 0, background: scrim }} /> : null}

      {/* Bloque de datos: anclado a la esquina y ajustado al ancho del
          contenido (así la línea del branding no cruza toda la tarjeta). */}
      {showData ? (
        <div
          style={{
            position: 'absolute',
            top: arriba ? 0 : undefined,
            bottom: arriba ? undefined : 0,
            left: izquierda ? 0 : undefined,
            right: izquierda ? undefined : 0,
            maxWidth: '92%',
            padding: 56,
            textAlign: izquierda ? 'left' : 'right',
          }}
        >
          {datos}
        </div>
      ) : null}

      {/* Logo de cliente en su esquina. */}
      {showLogo ? (
        <img
          src={logoDataUrl}
          alt=""
          style={{
            position: 'absolute',
            top: logoV === 'arriba' ? 10 : undefined,
            bottom: logoV === 'arriba' ? undefined : 10,
            left: logoH === 'izquierda' ? 10 : undefined,
            right: logoH === 'izquierda' ? undefined : 10,
            maxWidth: logoMax,
            maxHeight: logoMax,
            objectFit: 'contain',
          }}
        />
      ) : null}
    </div>
  );
});
